import React, { useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  TextField,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  Alert,
  Snackbar,
  Tooltip,
  Stack
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Edit as EditIcon } from "@mui/icons-material";
import TablePagination from "@mui/material/TablePagination";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import baseURL from "../../config";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const FormIModal = ({ contractDetails }) => {
  const [activeRowIndex, setActiveRowIndex] = useState(-1);
  const [cocDates, setCocDates] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loadingIndex, setLoadingIndex] = useState(-1);

  const userID = localStorage.getItem("user id");

  const handleStatusUpdate = async (contract, index) => {
    const cocDate = cocDates[index];
    if (!cocDate) {
      showToast("Please enter a date.", "error");
      return;
    }

    setLoadingIndex(index); // start loader

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${baseURL}/api/statusAfterForm1`,
        {
          contractId: contract.contractDetails?._id,
          coc: [{ Date: cocDate }],
          creatorId: userID,
          crewId: contract.contractDetails?.crewdetails?._id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
        }
      );

      showToast(response.data.message, "success");
      setActiveRowIndex(-1);
      setCocDates({ ...cocDates, [index]: "" });

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("Error updating status:", error);
      showToast(error.response?.data?.message || "Failed to update status", "error");
    } finally {
      setLoadingIndex(-1); // stop loader
    }
  };

  const showToast = (message, severity) => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredContracts = contractDetails.filter(contract =>
    contract.contractDetails?.ShipDetails?.vesselname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contract.contractDetails?.crewdetails?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contract.contractDetails?.crewingAgent?.agentName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Card elevation={3} sx={{ width: "100%", borderRadius: 2, overflow: "hidden" }}>
      <CardContent sx={{ p: 4 }}>
        <Box
          sx={{
            p: 1,
            bgcolor: "#C30B12",
            color: "white",
            display: "flex",
            alignItems: "center",
            position: "relative",
          }}
        >
          <TextField
            placeholder="Search by vessel, crew, or agent name..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              bgcolor: "#fff",
              borderRadius: 2,
              width: "300px",
            }}
          />

          <Typography
            variant="h6"
            component="h2"
            fontWeight="500"
            sx={{
              color: "white",
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              whiteSpace: "nowrap",
              
            }}
          >
            Form I Status Management
          </Typography>
        </Box>

        {filteredContracts.length > 0 ? (
          <>
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: 500,
                overflowY: "auto",
                overflowX: "auto",
                borderRadius: 2,
                boxShadow: "none",
                border: "1px solid #e0e0e0",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold" }}>Sr. No.</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold" }}>Contract Details</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold" }}>Form I</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", width: activeRowIndex !== -1 ? "340px" : "100px" }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredContracts
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((contract, index) => (
                      <TableRow hover key={index}>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            <strong style={{ color: "black" }}>Vessel Name: {contract.contractDetails?.ShipDetails?.vesselname || "N/A"}</strong>
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Chip
                              size="small"
                              label={`Crew: ${contract.contractDetails?.crewdetails?.name || "N/A"}`}
                              sx={{ bgcolor: "#e3f2fd", color: "#0d47a1" }}
                            />
                            <Chip
                              size="small"
                              label={`Indos No.: ${contract.contractDetails?.crewdetails?.indosno || "N/A"}`}
                              sx={{ bgcolor: "#fff9c4", color: "#f57f17" }}
                            />
                            <Chip
                              size="small"
                              label={`Agent: ${contract.contractDetails?.crewingAgent?.agentName || "N/A"}`}
                              sx={{ bgcolor: "#e8f5e9", color: "#1b5e20" }}
                            />
                          </Box>
                        </TableCell>

                        <TableCell>
                          {contract.contractStatus?.formI?.filePath ? (
                            <IconButton
                              onClick={() => window.open(`${baseURL}/${contract.contractStatus.formI.filePath.replace(/\\/g, '/')}`, "_blank")}
                            >
                              <PictureAsPdfIcon sx={{ color: "red" }} />
                            </IconButton>
                          ) : (
                            <Chip
                              label="No File Available"
                              color="error"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>

                        <TableCell>
                          {activeRowIndex === index ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <TextField
                                type="date"
                                label="COC Date"
                                InputLabelProps={{ shrink: true }}
                                value={cocDates[index] || ""}
                                onChange={(e) => setCocDates({ ...cocDates, [index]: e.target.value })}
                                size="small"
                                sx={{ width: "160px" }}
                                inputProps={{
                                  max: new Date().toISOString().split("T")[0],
                                }}
                              />
                              <Button
                                variant="contained"
                                // color="primary"
                                size="small"
                                onClick={() => handleStatusUpdate(contract, index)}
                                sx={{ borderRadius: 2 , backgroundColor:"#C30B12"}}
                                disabled={loadingIndex === index}
                              >
                                {loadingIndex === index ? (
                                  <CircularProgress size={20} color="inherit" />
                                ) : (
                                  "Submit"
                                )}
                              </Button>
                              <IconButton
                                size="small"
                                color="default"
                                onClick={() => setActiveRowIndex(-1)}
                                sx={{ ml: 1 }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          ) : (
                            <Tooltip title="Add COC Date">
                              <IconButton
                                
                                onClick={() => setActiveRowIndex(index)}
                                size="small"
                                sx={{
                                  "&:hover": { bgcolor: "#f5f5f5" },
                                  color:"#B8860B"
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredContracts.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                px: 2,
                bgcolor: "#fafafa",
                borderTop: "1px solid #e0e0e0",
                display: "flex",
                justifyContent: "flex-end"
              }}
            />
          </>
        ) : (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            No contract details found matching your search criteria.
          </Alert>
        )}
      </CardContent>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: "100%" }}>
          {toast.message}
        </Alert>
      </Snackbar>
      <ToastContainer />
    </Card>
  );
};

export default FormIModal;
